import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crosshair } from "@/components/Crosshair";
import useAsyncLoad from "@/hooks/useAsyncLoad";
import { Loading, LoadFailed } from "@/components/LoadState";
import { toast } from "@/lib/toast";
import {
  FileCheck, ShieldCheck, Lock, UploadCloud, CheckCircle2, Clock, AlertTriangle,
  RefreshCw, ArrowLeft, Building2,
} from "lucide-react";

const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm transition-colors";
const labelCls = "font-mono text-[11px] uppercase tracking-wide text-secondary-foreground block mb-1";

const FFL_TYPE_OPTS = [
  ["01", "01 — Dealer in firearms / gunsmith"],
  ["02", "02 — Pawnbroker in firearms"],
  ["03", "03 — Collector of Curios & Relics"],
  ["06", "06 — Manufacturer of ammunition"],
  ["07", "07 — Manufacturer of firearms"],
  ["08", "08 — Importer of firearms / ammunition"],
  ["09", "09 — Dealer in destructive devices"],
  ["10", "10 — Manufacturer of destructive devices"],
  ["11", "11 — Importer of destructive devices"],
];

// status -> chip. EXPIRED reads as a renewal prompt, never a punishment.
const STATUS_META = {
  not_submitted: { label: "Not submitted", cls: "border-sniper text-muted-foreground", Icon: FileCheck },
  pending_review: { label: "Pending review", cls: "border-[#7A5A12] text-[#E8C46A]", Icon: Clock },
  verified: { label: "Verified", cls: "border-[#1F6BA8] text-[#4a9fd6]", Icon: CheckCircle2 },
  expired: { label: "Renewal needed", cls: "border-[#C2600F] text-[#E8A05A]", Icon: RefreshCw },
  rejected: { label: "Needs changes", cls: "border-[#C2600F] text-[#E8A05A]", Icon: AlertTriangle },
};

function StatusChip({ status }) {
  const m = STATUS_META[status] || STATUS_META.not_submitted;
  const Icon = m.Icon;
  return (
    <span data-testid={`ffl-status-${status}`} className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide px-2 py-1 border ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function FflForm({ record, onSaved }) {
  const seed = record || {};
  const [fflNumber, setFflNumber] = useState(seed.ffl_number || "");
  const [fflType, setFflType] = useState(seed.ffl_type || "");
  const [expiration, setExpiration] = useState(seed.expiration_date || "");
  const [licensee, setLicensee] = useState(seed.licensee_name || "");
  const [premises, setPremises] = useState(seed.premises_address || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const isResubmit = !!record && record.status !== "not_submitted";

  const submit = async (e) => {
    e.preventDefault();
    if (!fflNumber.trim() || !fflType || !expiration || !licensee.trim() || !premises.trim()) {
      toast.error("Fill in every field."); return;
    }
    if (!file) { toast.error("Attach a copy of your FFL (PDF or a clear photo/scan)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("ffl_number", fflNumber.trim());
      fd.append("ffl_type", fflType);
      fd.append("expiration_date", expiration);
      fd.append("licensee_name", licensee.trim());
      fd.append("premises_address", premises.trim());
      fd.append("document", file);
      await api.post("/me/licenses/ffl", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("FFL submitted for review.");
      setFile(null);
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't submit your FFL.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="ffl-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>FFL number</label>
          <input value={fflNumber} onChange={(e) => setFflNumber(e.target.value)} data-testid="ffl-number-input" className={inputCls} placeholder="X-XX-XXX-XX-XX-XXXXX" />
        </div>
        <div>
          <label className={labelCls}>FFL type</label>
          <select value={fflType} onChange={(e) => setFflType(e.target.value)} data-testid="ffl-type-input" className={inputCls}>
            <option value="">— Select type —</option>
            {FFL_TYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Expiration date</label>
          <input type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} data-testid="ffl-expiration-input" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Licensee name (as printed on the FFL)</label>
          <input value={licensee} onChange={(e) => setLicensee(e.target.value)} data-testid="ffl-licensee-input" className={inputCls} placeholder="Exactly as on the license" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Premises address (as printed on the FFL)</label>
        <textarea value={premises} onChange={(e) => setPremises(e.target.value)} rows={2} data-testid="ffl-premises-input" className={inputCls} placeholder="Street, city, state, ZIP" />
      </div>
      <div>
        <label className={labelCls}>License document — PDF or a clear photo/scan (max 15MB)</label>
        <label data-testid="ffl-file-label" className="flex items-center gap-2 border border-dashed border-sniper hover:border-olive px-3 py-3 cursor-pointer transition-colors">
          <UploadCloud className="w-4 h-4 text-olive-bright" />
          <span className="font-mono text-xs text-secondary-foreground truncate">{file ? file.name : "Choose file…"}</span>
          <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" data-testid="ffl-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <p className="font-mono text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
          <Lock className="w-3 h-3 inline -mt-0.5" /> Stored privately. Only OpticSwap staff can open it, and every view is logged. Photo GPS is stripped on upload.
        </p>
      </div>
      <button type="submit" disabled={busy} data-testid="ffl-submit"
        className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-6 py-3 transition-colors">
        <FileCheck className="w-4 h-4" /> {busy ? "Submitting…" : isResubmit ? "Resubmit for review" : "Submit for review"}
      </button>
    </form>
  );
}

function FflCard({ record, onSaved }) {
  const status = record?.status || "not_submitted";
  const [editing, setEditing] = useState(status === "not_submitted");
  const showForm = editing || status === "not_submitted";

  return (
    <div className="border border-sniper bg-surface p-5" data-testid="license-card-ffl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileCheck className="w-5 h-5 text-olive-bright" />
          <h2 className="font-heading text-xl uppercase tracking-tight">Federal Firearms License (FFL)</h2>
        </div>
        <StatusChip status={status} />
      </div>

      {status === "verified" && record?.expiration_date && (
        <p className="text-xs font-body text-secondary-foreground mt-2" data-testid="ffl-verified-note">
          Verified{record.verified_at ? ` on ${new Date(record.verified_at).toLocaleDateString()}` : ""}. Expires {record.expiration_date}. We'll remind you before it lapses.
        </p>
      )}
      {status === "pending_review" && (
        <p className="text-xs font-body text-secondary-foreground mt-2" data-testid="ffl-pending-note">
          Submitted — our team will review it against your document. You'll hear back by email.
        </p>
      )}
      {status === "expired" && (
        <p className="text-xs font-body text-[#E8A05A] mt-2" data-testid="ffl-expired-note">
          The FFL on file expired ({record?.expiration_date}). Upload your current license below to keep your DEALER VERIFIED credential and map placement.
        </p>
      )}
      {status === "rejected" && record?.reject_reason && (
        <div className="mt-3 border border-[#C2600F]/50 bg-[#C2600F]/10 px-3 py-2.5" data-testid="ffl-reject-reason">
          <p className="font-mono text-[11px] uppercase text-[#E8A05A]">A note from our team</p>
          <p className="font-body text-[13px] text-secondary-foreground mt-1">{record.reject_reason}</p>
        </div>
      )}

      {!showForm && status !== "not_submitted" && (
        <div className="mt-4 space-y-1.5 font-mono text-[12px]" data-testid="ffl-summary">
          <div className="flex gap-2"><span className="text-muted-foreground w-28 shrink-0">Number</span><span className="text-foreground break-all">{record.ffl_number}</span></div>
          <div className="flex gap-2"><span className="text-muted-foreground w-28 shrink-0">Type</span><span className="text-foreground">{record.ffl_type}</span></div>
          <div className="flex gap-2"><span className="text-muted-foreground w-28 shrink-0">Expiration</span><span className="text-foreground">{record.expiration_date}</span></div>
          <div className="flex gap-2"><span className="text-muted-foreground w-28 shrink-0">Licensee</span><span className="text-foreground">{record.licensee_name}</span></div>
          <div className="flex gap-2"><span className="text-muted-foreground w-28 shrink-0">Premises</span><span className="text-foreground">{record.premises_address}</span></div>
          {record.document_filename && <div className="flex gap-2"><span className="text-muted-foreground w-28 shrink-0">Document</span><span className="text-foreground break-all">{record.document_filename}</span></div>}
        </div>
      )}

      {showForm ? (
        <div className={status === "not_submitted" ? "mt-4" : "mt-4 pt-4 border-t border-sniper"}>
          {status !== "not_submitted" && <Crosshair className="mb-4" />}
          <FflForm record={record} onSaved={() => { setEditing(false); onSaved(); }} />
        </div>
      ) : (
        <button onClick={() => setEditing(true)} data-testid="ffl-edit-btn"
          className="mt-4 inline-flex items-center gap-2 border border-olive/60 text-olive-bright hover:bg-olive/10 font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> {status === "verified" ? "Update FFL" : "Resubmit"}
        </button>
      )}
    </div>
  );
}

function ComingSoonCard({ title, blurb, testid }) {
  return (
    <div className="border border-sniper bg-surface p-5 opacity-80" data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileCheck className="w-5 h-5 text-muted-foreground" />
          <h2 className="font-heading text-xl uppercase tracking-tight text-secondary-foreground">{title}</h2>
        </div>
        <span className="font-mono text-[9px] uppercase tracking-wide px-2 py-1 border border-sniper text-muted-foreground">Coming soon</span>
      </div>
      <p className="text-xs font-body text-muted-foreground mt-2">{blurb}</p>
    </div>
  );
}

export default function DealerLicenses() {
  const { user } = useAuth();
  const isBusiness = user?.account_type === "business" || user?.is_dealer || user?.is_dealer_applicant;
  const { status, data, error, retry } = useAsyncLoad(
    (signal) => api.get("/me/licenses", { signal }).then((r) => r.data),
    []
  );

  const ffl = (data?.licenses || []).find((l) => l.kind === "ffl") || null;

  return (
    <div className="min-h-screen text-foreground" data-testid="dealer-licenses-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </Link>
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-olive-bright" />
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Licenses &amp; Compliance</h1>
        </div>
        <p className="text-sm text-secondary-foreground mt-1 font-body">
          Your business license records. A verified FFL earns your <span className="text-[#4a9fd6] font-semibold">DEALER VERIFIED</span> credential and, with a business address on file, puts you on the map. Documents are private, staff-only, and every view is logged.
        </p>

        <Crosshair className="my-6" />

        {!isBusiness && (
          <div className="border border-[#7A5A12] bg-[#1a1608] p-4 mb-5 flex items-start gap-3" data-testid="licenses-not-business">
            <Building2 className="w-5 h-5 text-[#E8C46A] shrink-0 mt-0.5" />
            <div>
              <p className="font-heading uppercase text-sm text-[#E8C46A]">Business accounts only</p>
              <p className="text-[13px] font-body text-secondary-foreground mt-1">
                License records are for business / dealer accounts. <Link to="/dealer/apply" className="text-olive-bright underline">Apply for a business account</Link> first.
              </p>
            </div>
          </div>
        )}

        {status === "loading" && <Loading label="Loading your licenses…" testid="licenses-loading" />}
        {status === "error" && <LoadFailed reason={error} onRetry={retry} testid="licenses-load-failed" />}
        {status === "ok" && (
          <div className="space-y-5">
            <FflCard record={ffl} onSaved={retry} />
            <ComingSoonCard title="SOT — Special Occupational Tax" blurb="Class 1/2/3 SOT for NFA items. Add it here once FFL verification is complete." testid="license-card-sot" />
            <ComingSoonCard title="State Business License" blurb="Your state business/reseller license. Coming soon." testid="license-card-state" />
            <ComingSoonCard title="Resale / Sales-Tax Certificate" blurb="Your resale or sales-tax certificate. Coming soon." testid="license-card-resale" />
          </div>
        )}
      </div>
    </div>
  );
}
